import { useCanWrite } from '@artic/api-client';
import { Button } from '@artic/ui/button';
import { DisabledReasonTooltip } from '@artic/ui/disabled-reason-tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditProjectDialog } from './edit-project-dialog';
import type { ProjectPlanningDetail } from './types';

interface EditProjectButtonProps {
  project: ProjectPlanningDetail;
}

/**
 * "Update Project" action + dialog pair. Enabled for draft projects and for
 * projects in `redrafting` — a stored `project_status` value (ENG-830), not a
 * derived one — unless the project is archived and so read-only (ENG-981).
 * Otherwise disabled, with a tooltip explaining the reason.
 *
 * ENG-720 renders it inline with the breadcrumb (Band 1) via the route's
 * `breadcrumbBadges` resolver, so it reads as a link rather than a button:
 * "Update" — not "Edit" — because committing a change is what the project
 * lifecycle calls a release.
 */
export function EditProjectButton({ project }: EditProjectButtonProps) {
  const { t } = useTranslation('project');
  const [open, setOpen] = useState(false);
  const canWritePlanner = useCanWrite('planner');
  const isDraft = project.project_status === 'draft';
  const canEditStatus = isDraft || project.project_status === 'redrafting';
  const disabledReason = project.is_archived
    ? t('archive.readOnly')
    : canEditStatus
      ? null
      : t('header.updateProjectDisabled');

  // Permissions v1: a read-only user never sees the entry point at all —
  // there's nothing they can commit through it.
  if (!canWritePlanner) return null;

  return (
    <>
      <DisabledReasonTooltip reason={disabledReason}>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setOpen(true)}
          disabled={disabledReason !== null}
        >
          {t('header.updateProject')}
        </Button>
      </DisabledReasonTooltip>
      <EditProjectDialog open={open} onOpenChange={setOpen} project={project} />
    </>
  );
}
